import React, { useRef, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./OurSolution.css";

import icon1 from "../../../../assets/icons/os1.svg";
import icon2 from "../../../../assets/icons/os2.svg";
import icon3 from "../../../../assets/icons/os3.svg";
import arrowLeft from "../../../../assets/icons/left.svg";
import arrowRight from "../../../../assets/icons/right.svg";
import { FiArrowUpRight } from "react-icons/fi";

const fallbackOurSolution = {
  tag: "OUR SOLUTIONS",
  titleLine1: "Integrated Systems",
  titleLine2: "for Every",
  titleLine3: "Construction Need",
  description:
    "IRQGYP delivers integrated solutions across every stage of construction, the complete solution from foundations to final finishes.",
  moreDetailsText: "More Details",
  item1Title: "Exterior System",
  item1Description:
    "IRQGYP delivers advanced exterior solutions for durable, high-performance, weather-resistant architecture.",
  item1Link: "/solutiondetails/exterior-system",
  item2Title: "Interior Drywall System",
  item2Description:
    "IRQGYP Drywall System: lightweight, acoustic, moisture-resistant walls with integrated services.",
  item2Link: "/solutiondetails/interior-drywall-system",
  item3Title: "Ceiling System",
  item3Description:
    "High-performance ceiling systems designed for durability and aesthetic integration.",
  item3Link: "/solutiondetails/ceiling-system",
};

const itemFieldRegex = /^item(\d+)([a-zA-Z0-9_]+)$/i;

const buildCardsFromContent = (content) => {
  const cardsMap = new Map();

  Object.entries(content || {}).forEach(([key, value]) => {
    const match = key.match(itemFieldRegex);
    if (!match) return;
    const index = Number(match[1]);
    const rawField = String(match[2] || "");
    const field = rawField.replace(/[_\s-]+/g, "").toLowerCase();
    if (!cardsMap.has(index)) cardsMap.set(index, {});
    cardsMap.get(index)[field] = value;
  });

  return Array.from(cardsMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([index, fields]) => ({
      index,
      title: fields.title || "",
      description: fields.description || "",
      link: fields.link || "",
    }))
    .filter((card) => card.title || card.description || card.link);
};

const hasSectionData = (section) => {
  if (!section || typeof section !== "object") return false;
  return Object.values(section).some((value) => {
    if (value === null || typeof value === "undefined") return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const OurSolution = ({ content }) => {
  const { i18n } = useTranslation();
  const sliderRef = useRef(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const [ourSolutionContent, setOurSolutionContent] = useState(fallbackOurSolution);
  const isRtl = i18n.dir(i18n.language) === "rtl";

  useEffect(() => {
    setOurSolutionContent(hasSectionData(content) ? content : fallbackOurSolution);
  }, [content]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current || !sliderRef.current) return;
      e.preventDefault();
      const el = sliderRef.current;
      el.scrollLeft = dragScrollLeft.current - (e.clientX - dragStartX.current);
    };

    const onUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      const el = sliderRef.current;
      if (el) {
        el.style.cursor = "";
        el.style.userSelect = "";
      }
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const onSliderMouseDown = (e) => {
    const el = sliderRef.current;
    if (!el) return;
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = el.scrollLeft;
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
    e.preventDefault();
  };

  const solutions = useMemo(
    () => {
      const dynamicCards = buildCardsFromContent(ourSolutionContent);
      if (dynamicCards.length) {
        return dynamicCards.map((card, idx) => ({
          ...card,
          icon: [icon1, icon2, icon3][idx] || icon1,
        }));
      }
      return [
        {
          title: fallbackOurSolution.item1Title,
          description: fallbackOurSolution.item1Description,
          link: fallbackOurSolution.item1Link,
          icon: icon1,
        },
        {
          title: fallbackOurSolution.item2Title,
          description: fallbackOurSolution.item2Description,
          link: fallbackOurSolution.item2Link,
          icon: icon2,
        },
        {
          title: fallbackOurSolution.item3Title,
          description: fallbackOurSolution.item3Description,
          link: fallbackOurSolution.item3Link,
          icon: icon3,
        },
      ];
    },
    [ourSolutionContent]
  );

  const scroll = (direction) => {
    const container = sliderRef.current;
    const scrollAmount = 360;
    const logicalDirection = isRtl
      ? direction === "left"
        ? "right"
        : "left"
      : direction;

    container.scrollBy({
      left: logicalDirection === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  return (
    <section className="solutions">
      <div className="solutions-container">

        <div className="solutions-left">
          <span className="tag">{ourSolutionContent.tag}</span>

          <h2>
            {ourSolutionContent.titleLine1} <br />
            {ourSolutionContent.titleLine2} <br />
            <span>{ourSolutionContent.titleLine3}</span>
          </h2>

          <p>
            {ourSolutionContent.description}
          </p>

          <div className="arrows">
            <button
              type="button"
              onClick={() => scroll("left")}
              className="arrowcontainer"
              aria-label="Scroll solutions left"
            >
              <img src={isRtl ? arrowRight : arrowLeft} alt="" draggable={false} />
            </button>
            <button
              type="button"
              onClick={() => scroll("right")}
              className="arrowcontainer"
              aria-label="Scroll solutions right"
            >
              <img src={isRtl ? arrowLeft : arrowRight} alt="" draggable={false} />
            </button>
          </div>
        </div>

        <div className="solutions-right">
          <div
            className="slider"
            ref={sliderRef}
            onMouseDown={onSliderMouseDown}
          >

            {solutions.map((item, index) => (
              <Link className="card" key={index} to={item.link}>

                <div className="card-top">
                  <div className="card-icon">
                    <img src={item.icon} alt={item.title} draggable={false} />
                  </div>
                </div>

                <div className="card-bottom">
                  <h3>{item.title}</h3>

                  <p>{item.description}</p>

                  <div className="more">
                    {ourSolutionContent.moreDetailsText}{" "}
                    <FiArrowUpRight />
                  </div>
                </div>

              </Link>
            ))}

          </div>
        </div>

      </div>
    </section>
  );
};

export default OurSolution;